import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Sparkles,
  MapPin,
  Calendar,
  Plus,
  Trash2,
  Info,
  DollarSign,
  Ticket,
  Settings,
  Image as ImageIcon,
  FileText,
  ArrowLeft,
  X,
  PlusCircle,
  ShieldCheck,
  Tag,
  Search,
  Pencil,
} from "lucide-react";
import { AgendaData, LineupData, SponsorData } from "../CreateQickEventV2/types";
import NewAgendaModalV3 from "./Components/NewAgendaModalV3";
import NewLineupModalV3 from "./Components/NewLineupModalV3";
import NewSponsorModalV3 from "./Components/NewSponsorModalV3";
import BasicsVenuesTabV3 from "./Components/BasicsVenuesTabV3";
import TicketArchitectTabV3 from "./Components/TicketArchitectTabV3";
import EventContentTabV3 from "./Components/EventContentTabV3";
import AdvancedSettingsTabV3 from "./Components/AdvancedSettingsTabV3";
import QuickEventStudioV3 from "./Components/QuickEventStudioV3";
import { toast, ToastContainer } from "react-toastify";
import PulseLoader from "react-spinners/PulseLoader";
import Background from "../../Login/Background";
import { useNightMode } from "../../../contexts/NightModeContext";
import {
  getAllCategories,
  getEventDraftById,
  createQuickEventV2,
} from "../../../api";
import CustomSelect from "../../../components/common/CustomSelect/CustomSelect";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

interface MockLocation {
  name: string;
  address: string;
  coordinates: string;
}

const MOCK_LOCATIONS: MockLocation[] = [
  { name: "The Masonic Center", address: "1111 California St, San Francisco, CA", coordinates: "37.7915° N, 122.4124° W" },
  { name: "Chase Center", address: "1 Warriors Way, San Francisco, CA", coordinates: "37.7680° N, 122.3877° W" },
  { name: "Moscone Center", address: "747 Howard St, San Francisco, CA", coordinates: "37.7841° N, 122.4014° W" },
  { name: "Bill Graham Civic Auditorium", address: "99 Grove St, San Francisco, CA", coordinates: "37.7781° N, 122.4174° W" },
  { name: "Palace of Fine Arts", address: "3601 Lyon St, San Francisco, CA", coordinates: "37.8029° N, 122.4484° W" },
  { name: "Metreon Venue", address: "135 4th St, San Francisco, CA", coordinates: "37.7844° N, 122.4035° W" },
  { name: "The Regency Ballroom", address: "1300 Van Ness Ave, San Francisco, CA", coordinates: "37.7884° N, 122.4219° W" },
];

export interface TicketTierV3 {
  id: string;
  name: string;
  type: "Individual" | "Group";
  price: number;
  capacity: number;
  ticketsSold?: number;
  groupSize?: number;
  description?: string;
  salesStart?: string;
  salesEnd?: string;
  maxPerOrder?: number;
  hidden?: boolean;
}

const CreateEventV3: React.FC = () => {
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const { isNightMode } = useNightMode();

  const isEditMode = Boolean(eventId);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);

  const [builderMode, setBuilderMode] = useState<"quick" | "full">("full");
  const [activeSubTab, setActiveSubTab] = useState<"basics" | "tickets" | "content" | "settings">("basics");

  const [name, setName] = useState("");
  const [category, setCategory] = useState("Technology & Design");
  const [categoriesList, setCategoriesList] = useState<any[]>([]);

  const [isSingleDay, setIsSingleDay] = useState(true);
  const [singleDate, setSingleDate] = useState("2026-10-15");
  const [startDate, setStartDate] = useState("2026-10-15");
  const [endDate, setEndDate] = useState("2026-10-16");
  const [startTime, setStartTime] = useState("09:00 AM");
  const [endTime, setEndTime] = useState("05:00 PM");

  const [posterImage, setPosterImage] = useState<string | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropScale, setCropScale] = useState(1);

  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");

  const [venueName, setVenueName] = useState("");
  const [venueLocationSearch, setVenueLocationSearch] = useState("");
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);
  const [selectedLocationDetail, setSelectedLocationDetail] = useState<MockLocation | null>(null);
  const [isCustomVenue, setIsCustomVenue] = useState(false);
  const [secondaryVenues, setSecondaryVenues] = useState<{ id: string; name: string; location: string }[]>([]);
  const [newSecName, setNewSecName] = useState("");
  const [newSecLoc, setNewSecLoc] = useState("");
  const [showAddSecondaryVenue, setShowAddSecondaryVenue] = useState(false);

  const [feePreference, setFeePreference] = useState<"Organizer Pays" | "Attendee Pays">("Attendee Pays");

  const [tiers, setTiers] = useState<TicketTierV3[]>([]);
  const [showTicketDrawer, setShowTicketDrawer] = useState(false);
  const [editingTicketIndex, setEditingTicketIndex] = useState<number | null>(null);
  const [showAdvancedTicketSettings, setShowAdvancedTicketSettings] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    name: "",
    type: "Individual" as "Individual" | "Group",
    price: 0,
    capacity: 100,
    groupSize: 4,
    description: "",
    salesStart: "",
    salesEnd: "",
    maxPerOrder: 10,
    hidden: false,
  });

  const [agendaSlots, setAgendaSlots] = useState<AgendaData[]>([]);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState<AgendaData | null>(null);

  const [lineupMembers, setLineupMembers] = useState<LineupData[]>([]);
  const [showLineupModal, setShowLineupModal] = useState(false);
  const [editingLineup, setEditingLineup] = useState<LineupData | null>(null);

  const [sponsorsList, setSponsorsList] = useState<SponsorData[]>([]);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<SponsorData | null>(null);

  const handleSaveAgenda = (item: AgendaData) => {
    if (editingAgenda) {
      setAgendaSlots(agendaSlots.map((a) => (a.id === item.id ? item : a)));
    } else {
      setAgendaSlots([...agendaSlots, item]);
    }
    setEditingAgenda(null);
  };

  const handleSaveLineup = (item: LineupData) => {
    if (editingLineup) {
      setLineupMembers(lineupMembers.map((l) => (l.id === item.id ? item : l)));
    } else {
      setLineupMembers([...lineupMembers, item]);
    }
    setEditingLineup(null);
  };

  const handleSaveSponsor = (item: SponsorData) => {
    if (editingSponsor) {
      setSponsorsList(sponsorsList.map((s) => (s.id === item.id ? item : s)));
    } else {
      setSponsorsList([...sponsorsList, item]);
    }
    setEditingSponsor(null);
  };

  const [faqList, setFaqList] = useState<{ id: string; q: string; a: string }[]>([]);
  const [newFaqQ, setNewFaqQ] = useState("");
  const [newFaqA, setNewFaqA] = useState("");

  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [newGalleryUrl, setNewGalleryUrl] = useState("");

  const [customURL, setCustomURL] = useState("");
  const [eventVisibility, setEventVisibility] = useState<"Public" | "Private" | "Password Protected">("Public");
  const [isPublishScheduled, setIsPublishScheduled] = useState(false);
  const [schedulePublishDate, setSchedulePublishDate] = useState("2026-10-01T12:00");

  const [requireAttendeeDetails, setRequireAttendeeDetails] = useState(true);
  const [requirePreRegistration, setRequirePreRegistration] = useState(false);
  const [requireTerms, setRequireTerms] = useState(false);
  const [termsText, setTermsText] = useState("");
  const [refundPolicy, setRefundPolicy] = useState("Non-refundable");
  const [password, setPassword] = useState("");

  const [coupons, setCoupons] = useState<{ id: string; code: string; discountType: "Percentage" | "Fixed"; discountValue: number; maxUses?: number; usesCount: number; isActive: boolean }[]>([]);
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newCouponType, setNewCouponType] = useState<"Percentage" | "Fixed">("Percentage");
  const [newCouponValue, setNewCouponValue] = useState(10);
  const [newCouponMaxUses, setNewCouponMaxUses] = useState("");

  const posterInputRef = useRef<HTMLInputElement | null>(null);
  const posterInputFullRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const catRes = await getAllCategories();
        const cats = Array.isArray(catRes) ? catRes : (catRes?.data || catRes?.categories || []);
        if (Array.isArray(cats)) setCategoriesList(cats);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }

      if (eventId) {
        try {
          setIsLoadingEvent(true);
          const res = await getEventDraftById(eventId);
          const eventData = res?.data?.event || res?.event || res?.data || res;
          if (eventData) {
            setName(eventData.eventName || eventData.name || "");
            setDescription(eventData.eventDescription || eventData.description || "");
            setShortDescription(eventData.shortDescription || "");
            setCategory(eventData.categoryName || eventData.category || "Technology & Design");
            if (eventData.eventDate) { setSingleDate(eventData.eventDate); setStartDate(eventData.eventDate); }
            if (eventData.endDate) { setEndDate(eventData.endDate); setIsSingleDay(eventData.eventDate === eventData.endDate); }
            if (eventData.eventTime) setStartTime(eventData.eventTime);
            if (eventData.endTime || eventData.eventEndTime) setEndTime(eventData.endTime || eventData.eventEndTime);
            const poster = eventData.bannerImgDesktop || eventData.eventImage || eventData.thumbImg;
            if (poster) setPosterImage(poster);
            setVenueName(eventData.eventVenue || eventData.venue || "");
            setVenueLocationSearch(eventData.eventLocation || eventData.eventAddress1 || "");
            const tickets = res?.data?.ticketTypes || res?.ticketTypes || eventData.tickets || [];
            if (Array.isArray(tickets) && tickets.length > 0) {
              setTiers(tickets.map((t: any, i: number) => ({
                id: String(t.id || i),
                name: t.ticketType || t.name || `Tier ${i + 1}`,
                type: t.isGroupTicket ? "Group" : "Individual",
                price: Number(t.price || 0),
                capacity: Number(t.totalTickets || t.capacity || 100),
                description: t.description || "",
                groupSize: t.groupTicketCount || 4,
              })));
            }
          }
        } catch (err) {
          console.error("Error loading event data for edit:", err);
          toast.error("Failed to load event data.");
        } finally {
          setIsLoadingEvent(false);
        }
      }
    };
    fetchInitialData();
  }, [eventId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPosterImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const selectSuggestion = (loc: MockLocation) => {
    setVenueName(loc.name);
    setVenueLocationSearch(loc.address);
    setSelectedLocationDetail(loc);
    setShowVenueSuggestions(false);
  };

  const handleOpenTicketDrawer = (index?: number) => {
    if (index !== undefined) {
      setEditingTicketIndex(index);
      const tier = tiers[index];
      setTicketForm({
        name: tier.name,
        type: tier.type || "Individual",
        price: tier.price,
        capacity: tier.capacity,
        groupSize: tier.groupSize || 4,
        description: tier.description || "",
        salesStart: tier.salesStart || "",
        salesEnd: tier.salesEnd || "",
        maxPerOrder: tier.maxPerOrder || 10,
        hidden: tier.hidden || false,
      });
    } else {
      setEditingTicketIndex(null);
      setTicketForm({ name: "", type: "Individual", price: 0, capacity: 100, groupSize: 4, description: "", salesStart: "", salesEnd: "", maxPerOrder: 10, hidden: false });
    }
    setShowAdvancedTicketSettings(false);
    setShowTicketDrawer(true);
  };

  const handleSaveTicket = () => {
    if (!ticketForm.name) return;
    const capacity = ticketForm.type === "Group" ? ticketForm.groupSize : ticketForm.capacity;
    const item: TicketTierV3 = {
      id: editingTicketIndex !== null ? tiers[editingTicketIndex].id : `t-${Date.now()}`,
      name: ticketForm.name,
      type: ticketForm.type,
      price: Number(ticketForm.price),
      capacity: Number(capacity),
      ticketsSold: editingTicketIndex !== null ? tiers[editingTicketIndex].ticketsSold : 0,
      groupSize: ticketForm.type === "Group" ? ticketForm.groupSize : undefined,
      description: ticketForm.description,
      salesStart: ticketForm.salesStart,
      salesEnd: ticketForm.salesEnd,
      maxPerOrder: Number(ticketForm.maxPerOrder),
      hidden: ticketForm.hidden,
    };
    if (editingTicketIndex !== null) {
      const updated = [...tiers];
      updated[editingTicketIndex] = item;
      setTiers(updated);
    } else {
      setTiers([...tiers, item]);
    }
    setShowTicketDrawer(false);
  };

  const handleDuplicateTicket = (index: number) => {
    const original = tiers[index];
    setTiers([...tiers, { ...original, id: `t-dup-${Date.now()}`, name: `${original.name} (Copy)` }]);
  };

  const handleRemoveTicket = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const createTierFromPreset = (presetName: string, price: number, type: "Individual" | "Group", groupSize?: number, desc?: string) => {
    setTiers([...tiers, {
      id: `t-preset-${Date.now()}`,
      name: presetName,
      price,
      capacity: type === "Group" ? (groupSize || 4) : 100,
      ticketsSold: 0,
      type,
      groupSize,
      description: desc || "Standard package inclusions apply.",
    }]);
  };

  const [showErrors, setShowErrors] = useState(false);

  const isDescriptionEmpty = (html: string) => {
    if (!html) return true;
    const stripped = html.replace(/<[^>]*>/g, "").trim();
    return stripped === "";
  };

  const handleSubmit = async (eventStatusStr: "active" | "draft") => {
    setShowErrors(true);
    if (!name.trim()) { toast.error("Event Name is required"); return; }
    if (builderMode === "quick") {
      if (isDescriptionEmpty(description)) { toast.error("Main Description is required"); return; }
      if (!shortDescription.trim()) { toast.error("Short Share Description is required"); return; }
    }
    if (tiers.length === 0) { toast.error("Please add at least one ticket tier."); return; }
    try {
      setIsLoading(true);
      const authUser = localStorage.getItem("authUser");
      let organizerId = "";
      if (authUser) {
        try { organizerId = JSON.parse(authUser)?.user?.organizerId || ""; } catch (e) {}
      }
      const fd = new FormData();
      if (eventId) fd.append("eventId", eventId);
      fd.append("eventName", name);
      fd.append("eventDescription", description);
      fd.append("shortDescription", shortDescription);
      fd.append("eventLocation", venueLocationSearch || venueName || "Online");
      fd.append("eventVenue", venueName || "Venue");
      fd.append("organizerId", organizerId);
      fd.append("eventStatus", eventStatusStr);
      fd.append("eventDate", isSingleDay ? singleDate : startDate);
      fd.append("endDate", isSingleDay ? singleDate : endDate);
      fd.append("eventTime", startTime);
      fd.append("eventEndTime", endTime);
      fd.append("isSingleDayEvent", String(isSingleDay));
      fd.append("isOtherAttendeeRequired", String(requireAttendeeDetails));
      fd.append("isPreRegistrationRequired", String(requirePreRegistration));
      fd.append("hasTermsAndConditions", String(requireTerms));
      fd.append("termsAndConditions", termsText);
      fd.append("refundPolicy", refundPolicy);
      fd.append("tickets", JSON.stringify(tiers.map((t, i) => ({
        name: t.name,
        type: t.type === "Group" ? "group" : "single",
        price: t.price,
        totalTickets: t.capacity,
        description: t.description || "",
        ticketOrder: i + 1,
        isGroupTicket: t.type === "Group",
        groupTicketCount: t.groupSize || null,
      }))));
      if (posterFile) fd.append("eventImage", posterFile, posterFile.name);
      const res = await createQuickEventV2(fd);
      if (res) {
        toast.success(`Event ${isEditMode ? "updated" : "created"} successfully!`);
        setTimeout(() => navigate("/events"), 1500);
      }
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${isEditMode ? "update" : "create"} event.`);
    } finally {
      setIsLoading(false);
    }
  };

  const totalCapacity = tiers.reduce((a, b) => a + (b.capacity || 0), 0);
  const maxRevenue = tiers.reduce((a, b) => a + (b.price || 0) * (b.capacity || 0), 0);

  const PosterControls = ({ inputId }: { inputId: string }) => (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", padding: 16, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, gap: 16, width: "100%", height: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ fontSize: 10, fontFamily: "monospace", color: "#F79B59", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>Poster Controls</span>
        {!posterImage ? (
          <button type="button" onClick={() => document.getElementById(inputId)?.click()} className="v3-btn-primary" style={{ fontSize: 12, padding: "10px 16px" }}>
            <ImageIcon size={14} /> Upload Image
          </button>
        ) : (
          <>
            <button type="button" onClick={() => document.getElementById(inputId)?.click()} className="v3-btn-secondary" style={{ fontSize: 11 }}>Replace Poster</button>
            <button type="button" onClick={() => setShowCropModal(true)} className="v3-btn-secondary" style={{ fontSize: 11 }}>Crop Poster</button>
            <button type="button" onClick={() => { setPosterImage(null); setPosterFile(null); }} className="v3-btn-secondary" style={{ fontSize: 11, color: "#FF5D73", borderColor: "rgba(255,93,115,0.2)" }}>Remove Poster</button>
          </>
        )}
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 12 }}>
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontStyle: "italic", marginBottom: 4 }}>It is preferred to have a 3:4 portrait or 4:3 portrait ratio image.</p>
        <p style={{ fontSize: 9, fontFamily: "monospace", color: "rgba(238,63,124,0.7)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, margin: 0 }}>Optimal: 1200 x 1600px</p>
      </div>
    </div>
  );

  return (
    <div className={`v3-wrap px-3 py-4 ${isNightMode ? "" : "light-theme"}`} style={{ position: "relative" }}>
      <Background />
      <ToastContainer position="top-right" autoClose={3000} theme={isNightMode ? "dark" : "light"} />

      {isLoadingEvent ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <PulseLoader color="#EE3F7C" size={14} />
        </div>
      ) : (
        <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button type="button" className="v3-btn-ghost" style={{ fontSize: 12 }} onClick={() => navigate("/events")}>
              <ArrowLeft size={14} /> Exit Event Creator
            </button>
            <span style={{ fontSize: 11, fontFamily: "monospace", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>
              {isEditMode ? `Editing Event ID: ${eventId}` : "New Creation Instance"}
            </span>
          </div>

          {/* Header */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 20 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                <Sparkles size={18} color="#EE3F7C" /> {isEditMode ? "Edit Quick Event" : "Create Quick Event"}
              </h1>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4, marginBottom: 0 }}>
                Choose a workflow layout that matches your immediate operational scale.
              </p>
            </div>
            <div style={{ display: "flex", background: "rgba(255,255,255,0.05)", padding: 4, borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)" }}>
              <button type="button" className={`v3-mode-tab ${builderMode === "quick" ? "active" : ""}`} onClick={() => setBuilderMode("quick")}>Quick Event (2 min)</button>
              <button type="button" className={`v3-mode-tab ${builderMode === "full" ? "active" : ""}`} onClick={() => setBuilderMode("full")}>Full Event Studio</button>
            </div>
          </div>

          {/* Sub Tabs */}
          {builderMode === "full" && (
            <div style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 1 }}>
              {(["basics", "tickets", "content", "settings"] as const).map((tab) => (
                <button key={tab} type="button" className={`v3-sub-tab ${activeSubTab === tab ? "active" : ""}`} onClick={() => setActiveSubTab(tab)}>
                  {tab === "basics" && "1. Basics & Venues"}
                  {tab === "tickets" && "2. Ticket Architect"}
                  {tab === "content" && "3. Event Content"}
                  {tab === "settings" && "4. Advanced Controls"}
                </button>
              ))}
            </div>
          )}

          {/* Main Grid */}
          <div className="v3-layout">
            <div className="v3-main">

              {/* ── QUICK MODE STAGE ── */}
              {builderMode === "quick" && (
                <QuickEventStudioV3
                  basicsProps={{
                    builderMode,
                    name, setName, category, setCategory, categoriesList,
                    isSingleDay, setIsSingleDay, singleDate, setSingleDate,
                    startDate, setStartDate, endDate, setEndDate,
                    startTime, setStartTime, endTime, setEndTime,
                    posterImage, setPosterImage, setPosterFile, setShowCropModal, cropScale,
                    description, setDescription, shortDescription, setShortDescription,
                    venueName, setVenueName, venueLocationSearch, setVenueLocationSearch,
                    showVenueSuggestions, setShowVenueSuggestions, selectedLocationDetail, setSelectedLocationDetail,
                    isCustomVenue, setIsCustomVenue, secondaryVenues, setSecondaryVenues,
                    newSecName, setNewSecName, newSecLoc, setNewSecLoc, showAddSecondaryVenue, setShowAddSecondaryVenue,
                    isNightMode, posterInputRef, showErrors
                  }}
                  ticketProps={{
                    builderMode, feePreference, setFeePreference, tiers,
                    handleOpenTicketDrawer, handleDuplicateTicket, handleRemoveTicket, createTierFromPreset
                  }}
                />
              )}

              {/* ── FULL MODE STAGES ── */}
              {builderMode === "full" && activeSubTab === "basics" && (
                <BasicsVenuesTabV3
                  builderMode={builderMode}
                  name={name} setName={setName} category={category} setCategory={setCategory} categoriesList={categoriesList}
                  isSingleDay={isSingleDay} setIsSingleDay={setIsSingleDay} singleDate={singleDate} setSingleDate={setSingleDate}
                  startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
                  startTime={startTime} setStartTime={setStartTime} endTime={endTime} setEndTime={setEndTime}
                  posterImage={posterImage} setPosterImage={setPosterImage} setPosterFile={setPosterFile} setShowCropModal={setShowCropModal} cropScale={cropScale}
                  description={description} setDescription={setDescription} shortDescription={shortDescription} setShortDescription={setShortDescription}
                  venueName={venueName} setVenueName={setVenueName} venueLocationSearch={venueLocationSearch} setVenueLocationSearch={setVenueLocationSearch}
                  showVenueSuggestions={showVenueSuggestions} setShowVenueSuggestions={setShowVenueSuggestions} selectedLocationDetail={selectedLocationDetail} setSelectedLocationDetail={setSelectedLocationDetail}
                  isCustomVenue={isCustomVenue} setIsCustomVenue={setIsCustomVenue} secondaryVenues={secondaryVenues} setSecondaryVenues={setSecondaryVenues}
                  newSecName={newSecName} setNewSecName={setNewSecName} newSecLoc={newSecLoc} setNewSecLoc={setNewSecLoc} showAddSecondaryVenue={showAddSecondaryVenue} setShowAddSecondaryVenue={setShowAddSecondaryVenue}
                  isNightMode={isNightMode} posterInputRef={posterInputFullRef} showErrors={showErrors}
                />
              )}

              {builderMode === "full" && activeSubTab === "tickets" && (
                <TicketArchitectTabV3
                  builderMode={builderMode} feePreference={feePreference} setFeePreference={setFeePreference} tiers={tiers}
                  handleOpenTicketDrawer={handleOpenTicketDrawer} handleDuplicateTicket={handleDuplicateTicket} handleRemoveTicket={handleRemoveTicket} createTierFromPreset={createTierFromPreset}
                />
              )}

              {builderMode === "full" && activeSubTab === "content" && (
                <EventContentTabV3
                  agendaSlots={agendaSlots} setAgendaSlots={setAgendaSlots} setShowAgendaModal={setShowAgendaModal} setEditingAgenda={setEditingAgenda}
                  lineupMembers={lineupMembers} setLineupMembers={setLineupMembers} setShowLineupModal={setShowLineupModal} setEditingLineup={setEditingLineup}
                  sponsorsList={sponsorsList} setSponsorsList={setSponsorsList} setShowSponsorModal={setShowSponsorModal} setEditingSponsor={setEditingSponsor}
                />
              )}

              {builderMode === "full" && activeSubTab === "settings" && (
                <AdvancedSettingsTabV3
                  feePreference={feePreference}
                  requireAttendeeDetails={requireAttendeeDetails} setRequireAttendeeDetails={setRequireAttendeeDetails}
                  requirePreRegistration={requirePreRegistration} setRequirePreRegistration={setRequirePreRegistration}
                  requireTerms={requireTerms} setRequireTerms={setRequireTerms} termsText={termsText} setTermsText={setTermsText}
                  refundPolicy={refundPolicy} setRefundPolicy={setRefundPolicy}
                />
              )}
            </div>

            {/* Right Sidebar */}
            <div className="v3-sidebar">
              <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <h3 style={{ fontSize: 12, fontFamily: "monospace", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid rgba(255,255,255,0.05)", paddingBottom: 10, margin: 0 }}>Financial &amp; Audience Est.</h3>

                <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "rgba(255,255,255,0.5)" }}>Allocated Seating Capacity:</span>
                    <span style={{ fontWeight: 700, color: "#fff", fontFamily: "monospace", display: "flex", alignItems: "center", gap: 4 }}><Ticket size={12} color="#F79B59" /> {totalCapacity} seats</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "rgba(255,255,255,0.5)" }}>Active Price Tiers:</span>
                    <span style={{ fontWeight: 700, color: "#fff", fontFamily: "monospace" }}>{tiers.length} configured structures</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 12 }}>
                    <span style={{ color: "rgba(255,255,255,0.5)" }}>Potential Net Revenue Target:</span>
                    <span className="text-gradient" style={{ fontWeight: 700, fontSize: 14, fontFamily: "monospace" }}>${maxRevenue.toLocaleString()}</span>
                  </div>
                </div>

                {/* Quick mode policy toggles in sidebar */}
                {builderMode === "quick" && (
                  <>
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <DollarSign size={12} color="#EE3F7C" />
                        <h4 style={{ fontSize: 10, fontFamily: "monospace", color: "#fff", textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>Ticketing Fee Rules</h4>
                      </div>
                      <div style={{ display: "flex", background: "rgba(255,255,255,0.02)", padding: 3, borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)" }}>
                        {(["Attendee Pays", "Organizer Pays"] as const).map((opt) => (
                          <button key={opt} type="button" onClick={() => setFeePreference(opt)} style={{ flex: 1, padding: "7px 8px", fontSize: 10, fontWeight: 700, borderRadius: 8, border: "none", cursor: "pointer", transition: "all 0.15s", background: feePreference === opt ? "linear-gradient(135deg, #EE3F7C, #F79B59)" : "transparent", color: feePreference === opt ? "#fff" : "rgba(255,255,255,0.45)" }}>{opt}</button>
                        ))}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>
                            {feePreference === "Attendee Pays" ? "Attendee Pays Fees" : "Organizer Absorbs Fees"}
                          </div>
                          <span className="text-primary" style={{ cursor: "pointer", fontSize: 10, whiteSpace: "nowrap" }}>Learn more</span>
                        </div>
                        <p style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", margin: 0, lineHeight: "1.4" }}>
                          {feePreference === "Attendee Pays" 
                            ? "Ticketing fees are added to the attendee's checkout total. You receive the full face-value amount of each ticket." 
                            : "Fees are absorbed into the ticket price. Payouts deduct booking costs directly from ticket revenue."}
                        </p>
                      </div>
                    </div>
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <ShieldCheck size={12} color="#EE3F7C" />
                        <h4 style={{ fontSize: 10, fontFamily: "monospace", color: "#fff", textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>Event Settings</h4>
                      </div>
                      {[
                        { label: "Require Information for All Attendees", sub: "When enabled, customers must provide information for each ticket holder. When disabled, only the primary attendee information is required.", state: requireAttendeeDetails, toggle: () => setRequireAttendeeDetails(!requireAttendeeDetails) },
                        { label: "Is Pre-Registration Required?", sub: "When enabled, users must pre-register before they can book tickets.", state: requirePreRegistration, toggle: () => setRequirePreRegistration(!requirePreRegistration) },
                        { label: "Add Terms & Conditions", sub: "When enabled, customers must agree to your terms and conditions before completing their booking.", state: requireTerms, toggle: () => setRequireTerms(!requireTerms) },
                      ].map(({ label, sub, state, toggle }) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                          <div>
                            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", display: "block" }}>{label}</span>
                            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.35)" }}>{sub}</span>
                          </div>
                          <button type="button" className={`v3-toggle ${state ? "on" : "off"}`} onClick={toggle}><span className="v3-toggle-knob" /></button>
                        </div>
                      ))}
                      {requireTerms && (
                        <textarea rows={2} className="v3-input" value={termsText} onChange={(e) => setTermsText(e.target.value)} placeholder="Enter rules or policy agreement terms..." style={{ fontSize: 10 }} />
                      )}
                    </div>
                  </>
                )}

                <div className="v3-notice">
                  <Info size={15} color="#EE3F7C" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>All new event entries start as Draft files. Click <strong>Publish Immediately</strong> to deploy active passes directly to checkout listings.</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
                  <button type="button" onClick={() => handleSubmit("draft")} disabled={isLoading} className="v3-btn-secondary" style={{ justifyContent: "center", padding: "12px 0", fontSize: 13 }}>
                    Save as Draft
                  </button>
                  <button type="button" onClick={() => handleSubmit("active")} disabled={isLoading} className="v3-btn-primary" style={{ justifyContent: "center", padding: "13px 0", fontSize: 13 }}>
                    {isLoading ? <PulseLoader color="#fff" size={8} /> : isEditMode ? "Update & Save Event" : "Publish Immediately"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Drawer */}
      {showTicketDrawer && (
        <div className="v3-drawer-backdrop">
          <div className="v3-drawer-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.07)", paddingBottom: 14, marginBottom: 20 }}>
              <h3 style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em", color: "#fff", margin: 0 }}>
                {editingTicketIndex !== null ? "Configure Ticket Tier" : "Add Ticket Tier"}
              </h3>
              <button type="button" onClick={() => setShowTicketDrawer(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 4, borderRadius: "50%" }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="v3-label">Ticket Tier Name</label>
                <input type="text" className="v3-input" placeholder="e.g. VIP Admissions" value={ticketForm.name} onChange={(e) => setTicketForm({ ...ticketForm, name: e.target.value })} />
              </div>

              <div>
                <label className="v3-label">Structure Type</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {(["Individual", "Group"] as const).map((t) => (
                    <button key={t} type="button" onClick={() => setTicketForm({ ...ticketForm, type: t })} style={{ padding: "10px 0", fontSize: 12, fontWeight: 700, borderRadius: 10, border: `1px solid ${ticketForm.type === t ? "#EE3F7C" : "rgba(255,255,255,0.07)"}`, background: ticketForm.type === t ? "rgba(238,63,124,0.12)" : "rgba(255,255,255,0.04)", color: "#fff", cursor: "pointer" }}>
                      {t === "Individual" ? "Individual Pass" : "Group Bundle"}
                    </button>
                  ))}
                </div>
              </div>

              {ticketForm.type === "Individual" ? (
                <div>
                  <label className="v3-label">Available Quantity</label>
                  <input type="number" className="v3-input" value={ticketForm.capacity} onChange={(e) => setTicketForm({ ...ticketForm, capacity: Number(e.target.value) })} />
                </div>
              ) : (
                <div>
                  <label className="v3-label">People Per Group</label>
                  <input type="number" className="v3-input" value={ticketForm.groupSize} onChange={(e) => setTicketForm({ ...ticketForm, groupSize: Number(e.target.value) })} />
                </div>
              )}

              <div>
                <label className="v3-label">Ticket Price (USD)</label>
                <input type="number" className="v3-input" value={ticketForm.price} onChange={(e) => setTicketForm({ ...ticketForm, price: Number(e.target.value) })} />
              </div>

              <div>
                <label className="v3-label">Description (Optional)</label>
                <textarea rows={2} className="v3-input" placeholder="Briefly state benefits, perk inclusions, or entry rules..." value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} />
              </div>

              <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 14 }}>
                <button type="button" onClick={() => setShowAdvancedTicketSettings(!showAdvancedTicketSettings)} style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  {showAdvancedTicketSettings ? "Hide" : "Show"} Advanced Ticket Settings ↓
                </button>
                {showAdvancedTicketSettings && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="v3-label">Sales Start Date</label>
                        <input type="datetime-local" className="v3-input" style={{ fontSize: 11 }} value={ticketForm.salesStart} onChange={(e) => setTicketForm({ ...ticketForm, salesStart: e.target.value })} />
                      </div>
                      <div className="col-6">
                        <label className="v3-label">Sales End Date</label>
                        <input type="datetime-local" className="v3-input" style={{ fontSize: 11 }} value={ticketForm.salesEnd} onChange={(e) => setTicketForm({ ...ticketForm, salesEnd: e.target.value })} />
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="v3-label">Max Per Order</label>
                        <input type="number" className="v3-input" value={ticketForm.maxPerOrder} onChange={(e) => setTicketForm({ ...ticketForm, maxPerOrder: Number(e.target.value) })} />
                      </div>
                      <div className="col-6" style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 20 }}>
                        <input type="checkbox" checked={ticketForm.hidden} onChange={(e) => setTicketForm({ ...ticketForm, hidden: e.target.checked })} style={{ accentColor: "#EE3F7C" }} />
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Hide Ticket from Public</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 16, marginTop: 20, display: "flex", gap: 8 }}>
              <button type="button" onClick={() => setShowTicketDrawer(false)} style={{ flex: 1, padding: "10px 0", textAlign: "center", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.55)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={handleSaveTicket} className="v3-btn-primary" style={{ flex: 1, justifyContent: "center", padding: "10px 0" }}>Save Ticket Tier</button>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "100%", maxWidth: 420, background: "#070711", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.9)", display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h4 style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em", color: "#fff", margin: 0 }}>Crop Event Poster</h4>
              <button type="button" onClick={() => setShowCropModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 4 }}><X size={15} /></button>
            </div>
            <div style={{ height: 192, background: "#000", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={posterImage || ""} alt="To Crop" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", transform: `scale(${cropScale})` }} />
              <div style={{ position: "absolute", inset: 16, border: "2px dashed #EE3F7C", pointerEvents: "none", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 10, fontFamily: "monospace", color: "#EE3F7C", background: "rgba(0,0,0,0.65)", padding: "2px 8px", borderRadius: 4 }}>Crop Area Grid</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontFamily: "monospace", color: "rgba(255,255,255,0.45)" }}>
                <span>Scale Zoom</span><span>{cropScale.toFixed(1)}x</span>
              </div>
              <input type="range" min="1" max="3" step="0.1" value={cropScale} onChange={(e) => setCropScale(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#EE3F7C" }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => { setCropScale(1); setShowCropModal(false); }} style={{ flex: 1, padding: "10px 0", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={() => setShowCropModal(false)} className="v3-btn-primary" style={{ flex: 1, justifyContent: "center", padding: "10px 0" }}>Apply Crop</button>
            </div>
          </div>
        </div>
      )}

      {/* V3 Content Modals */}
      <NewAgendaModalV3
        isOpen={showAgendaModal}
        toggle={() => setShowAgendaModal(false)}
        editingAgenda={editingAgenda}
        onSubmit={handleSaveAgenda}
      />
      <NewLineupModalV3
        isOpen={showLineupModal}
        toggle={() => setShowLineupModal(false)}
        editingLineup={editingLineup}
        onSubmit={handleSaveLineup}
      />
      <NewSponsorModalV3
        isOpen={showSponsorModal}
        toggle={() => setShowSponsorModal(false)}
        editingSponsor={editingSponsor}
        onSubmit={handleSaveSponsor}
      />
    </div>
  );
};

export default CreateEventV3;
